import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, MapPin, Calendar, Users, Building2, Wallet, CreditCard } from "lucide-react";
import api, { formatDate, resolveImageUrl } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { track } from "@/lib/analytics";

const MAX_POLLS = 18; // 90s
const INTERVAL_MS = 5000;

export default function BookingSuccessPage() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const bookingId = params.get("booking_id");
    const paymentMethodParam = params.get("pm"); // bank_transfer | at_hotel | null (stripe path)
    const { format, banks } = useCurrency();
    const [status, setStatus] = useState(paymentMethodParam ? "non_stripe" : "polling");
    const [booking, setBooking] = useState(null);
    const pollsRef = useRef(0);

    useEffect(() => {
        // Non-stripe flows: fetch booking directly by id
        if (paymentMethodParam && bookingId) {
            api.get(`/bookings/${bookingId}`).then((r) => {
                setBooking(r.data);
                track("booking_completed", {
                    booking_id: r.data.id,
                    hotel_id: r.data.hotel_id,
                    hotel_name: r.data.hotel_name,
                    amount: r.data.total_amount,
                });
            }).catch(() => setStatus("failed"));
            return;
        }
        if (!sessionId) {
            setStatus("failed");
            return;
        }
        let cancel = false;
        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
                if (cancel) return;
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    setBooking(data.booking);
                    track("booking_completed", {
                        booking_id: data.booking?.id,
                        hotel_id: data.booking?.hotel_id,
                        hotel_name: data.booking?.hotel_name,
                        amount: data.booking?.total_amount,
                    });
                    return;
                }
                if (data.status === "expired" || data.payment_status === "failed") {
                    setStatus("failed");
                    return;
                }
                pollsRef.current += 1;
                if (pollsRef.current >= MAX_POLLS) {
                    setStatus("timeout");
                    return;
                }
                setTimeout(poll, INTERVAL_MS);
            } catch {
                setStatus("failed");
            }
        };
        poll();
        return () => { cancel = true; };
    }, [sessionId, bookingId, paymentMethodParam]);

    const selectedBank = booking?.selected_bank_id ? banks.find((b) => b.id === booking.selected_bank_id) : null;

    return (
        <div data-testid="success-page" className="bg-slate-50 dark:bg-slate-950 min-h-screen">
            <Navbar />
            <div className="container mx-auto px-4 max-w-3xl py-16">
                {status === "non_stripe" && booking && (
                    <div data-testid="status-non-stripe" className="text-center">
                        <div className={`h-20 w-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
                            booking.payment_method === "at_hotel" ? "bg-emerald-100 dark:bg-emerald-950/50"
                            : booking.payment_method === "card" ? "bg-sky-100 dark:bg-sky-950/50"
                            : "bg-amber-100 dark:bg-amber-950/50"
                        }`}>
                            {booking.payment_method === "at_hotel" ? <Wallet className="h-12 w-12 text-emerald-500" />
                            : booking.payment_method === "card" ? <CreditCard className="h-12 w-12 text-sky-500" />
                            : <Building2 className="h-12 w-12 text-amber-500" />}
                        </div>
                        <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-white">
                            {booking.payment_method === "at_hotel" ? "Rezervasyonunuz onaylandı! 🎉"
                            : booking.payment_method === "card" ? "Kart ödemesine yönlendiriliyorsunuz..."
                            : "Rezervasyonunuz alındı! Havale bekleniyor"}
                        </h1>
                        <p className="text-slate-500 mt-2">Onay bilgileri <span className="font-semibold text-slate-700 dark:text-slate-200">{booking.guest.email}</span> adresine gönderildi.</p>

                        {booking.payment_method === "card" && (
                            <div className="mt-6 mx-auto max-w-xl text-left rounded-2xl bg-sky-50 dark:bg-sky-950/30 border-2 border-sky-200 dark:border-sky-900 p-5">
                                <div className="flex items-center gap-2 mb-3">
                                    <CreditCard className="h-5 w-5 text-sky-600" />
                                    <h3 className="font-display font-bold text-sky-900 dark:text-sky-200">Sanal POS Kart Ödemesi</h3>
                                </div>
                                <p className="text-xs text-sky-800 dark:text-sky-300 mb-3">
                                    Rezervasyonunuz <span className="font-bold">{booking.id.slice(0, 8).toUpperCase()}</span> kodu ile alındı.
                                    Tutar: <span className="font-bold">{format(booking.total_amount)}</span>
                                    {booking.payment_bank && <> · Banka: <span className="font-bold">{(booking.payment_bank || "").toUpperCase()}</span></>}
                                </p>
                                <div className="rounded-xl bg-white dark:bg-slate-900 border border-sky-200 dark:border-sky-900 p-4 text-sm text-slate-700 dark:text-slate-200">
                                    <p className="font-semibold mb-2">⚠️ Sanal POS Entegrasyonu Henüz Aktif Değil</p>
                                    <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                                        Müşteri temsilcimiz sizinle <span className="font-bold">{booking.guest.phone}</span> numarasından
                                        en kısa sürede iletişime geçerek ödeme linkini iletecektir. Acil durumlar için bizi
                                        +90 532 000 00 00 numarasından arayabilirsiniz.
                                    </p>
                                </div>
                            </div>
                        )}

                        {booking.payment_method === "bank_transfer" && selectedBank && (
                            <div className="mt-6 mx-auto max-w-xl text-left rounded-2xl bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-200 dark:border-amber-900 p-5">
                                <div className="flex items-center gap-2 mb-3">
                                    <Building2 className="h-5 w-5 text-amber-600" />
                                    <h3 className="font-display font-bold text-amber-900 dark:text-amber-200">Havale Bilgileri</h3>
                                </div>
                                <p className="text-xs text-amber-800 dark:text-amber-300 mb-3">
                                    Rezervasyonunuzun onaylanması için <span className="font-bold">48 saat içinde</span> aşağıdaki hesaba <span className="font-bold">{format(booking.total_amount)}</span> tutarında havale gönderin. Açıklamaya <span className="font-mono font-bold">{booking.id.slice(0, 8).toUpperCase()}</span> PNR kodunu yazmayı unutmayın.
                                </p>
                                <div className="space-y-1.5 text-sm text-slate-700 dark:text-slate-200">
                                    <div><span className="text-xs uppercase tracking-widest font-bold text-slate-500">Banka:</span> {selectedBank.bank_name}</div>
                                    <div><span className="text-xs uppercase tracking-widest font-bold text-slate-500">Hesap Sahibi:</span> {selectedBank.account_holder}</div>
                                    {selectedBank.branch && <div><span className="text-xs uppercase tracking-widest font-bold text-slate-500">Şube:</span> {selectedBank.branch}</div>}
                                    <div className="pt-1">
                                        <span className="text-xs uppercase tracking-widest font-bold text-slate-500">IBAN:</span>
                                        <code className="ml-2 px-2 py-1 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 font-mono text-sm">{selectedBank.iban}</code>
                                    </div>
                                </div>
                            </div>
                        )}

                        {booking.payment_method === "at_hotel" && (
                            <p className="mt-4 text-sm text-emerald-700 dark:text-emerald-300 max-w-xl mx-auto bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 px-4 py-3 rounded-xl">
                                Ödemeyi otele girişte ({formatDate(booking.check_in)}) yapacaksınız. Otel resepsiyonuna PNR kodunuzu (<span className="font-mono font-bold">{booking.id.slice(0, 8).toUpperCase()}</span>) gösterin.
                            </p>
                        )}

                        <div className="mt-8 text-left rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                            <img src={resolveImageUrl(booking.hotel_image)} alt="" className="w-full h-48 object-cover" />
                            <div className="p-6 space-y-4">
                                <div>
                                    <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white">{booking.hotel_name}</h2>
                                    <div className="flex items-center gap-1 text-sm text-slate-500 mt-1"><MapPin className="h-4 w-4" /> {booking.destination_name}</div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <Field icon={<Calendar className="h-4 w-4" />} label="Giriş" value={formatDate(booking.check_in)} />
                                    <Field icon={<Calendar className="h-4 w-4" />} label="Çıkış" value={formatDate(booking.check_out)} />
                                    <Field icon={<Users className="h-4 w-4" />} label="Misafir" value={`${booking.adults} yet, ${booking.children} çoc`} />
                                    <Field icon={<Users className="h-4 w-4" />} label="Oda" value={`${booking.rooms} × ${booking.room_type_name}`} />
                                </div>
                                <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center">
                                    <span className="text-slate-500">Rezervasyon No</span>
                                    <span className="font-mono text-sm font-semibold text-slate-900 dark:text-white" data-testid="booking-id">{booking.id.slice(0, 8).toUpperCase()}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="font-semibold text-slate-900 dark:text-white">{
                                        booking.payment_method === "at_hotel" ? "Otelde Ödenecek"
                                        : booking.payment_method === "card" ? "Kart ile Ödenecek"
                                        : "Beklenen Havale"
                                    }</span>
                                    <span className="text-2xl font-display font-bold text-sky-600">{format(booking.total_amount)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 flex gap-3 justify-center">
                            <Link to="/" className="px-6 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white font-semibold">Ana Sayfa</Link>
                            <Link to="/my-bookings" className="px-6 py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-semibold">Rezervasyonlarım</Link>
                        </div>
                    </div>
                )}

                {status === "polling" && (
                    <div data-testid="status-polling" className="text-center py-20">
                        <Loader2 className="h-14 w-14 animate-spin text-sky-500 mx-auto mb-4" />
                        <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">Ödemeniz onaylanıyor...</h1>
                        <p className="text-slate-500 mt-2">Lütfen sayfayı kapatmayın, bu birkaç saniye sürebilir.</p>
                    </div>
                )}

                {status === "paid" && booking && (
                    <div data-testid="status-paid" className="text-center">
                        <div className="h-20 w-20 rounded-full bg-emerald-100 dark:bg-emerald-950/50 flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
                        </div>
                        <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-white">Rezervasyonunuz onaylandı! 🎉</h1>
                        <p className="text-slate-500 mt-2">Onay bilgileri <span className="font-semibold text-slate-700 dark:text-slate-200">{booking.guest.email}</span> adresine gönderildi.</p>

                        <div className="mt-10 text-left rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                            <img src={resolveImageUrl(booking.hotel_image)} alt="" className="w-full h-48 object-cover" />
                            <div className="p-6 space-y-4">
                                <div>
                                    <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white">{booking.hotel_name}</h2>
                                    <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                                        <MapPin className="h-4 w-4" /> {booking.destination_name}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <Field icon={<Calendar className="h-4 w-4" />} label="Giriş" value={formatDate(booking.check_in)} />
                                    <Field icon={<Calendar className="h-4 w-4" />} label="Çıkış" value={formatDate(booking.check_out)} />
                                    <Field icon={<Users className="h-4 w-4" />} label="Misafir" value={`${booking.adults} yet, ${booking.children} çoc`} />
                                    <Field icon={<Users className="h-4 w-4" />} label="Oda" value={`${booking.rooms} × ${booking.room_type_name}`} />
                                </div>
                                <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center">
                                    <span className="text-slate-500">Rezervasyon No</span>
                                    <span className="font-mono text-sm font-semibold text-slate-900 dark:text-white" data-testid="booking-id">{booking.id.slice(0, 8).toUpperCase()}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="font-semibold text-slate-900 dark:text-white">Ödenen Tutar</span>
                                    <span className="text-2xl font-display font-bold text-sky-600">{format(booking.total_amount)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 flex gap-3 justify-center">
                            <Link data-testid="go-home" to="/" className="px-6 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white font-semibold">Ana Sayfa</Link>
                            <Link data-testid="go-my-bookings" to="/my-bookings" className="px-6 py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-semibold">Rezervasyonlarım</Link>
                        </div>
                    </div>
                )}

                {(status === "failed" || status === "timeout") && (
                    <div data-testid={`status-${status}`} className="text-center py-20">
                        <div className="h-20 w-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                            <XCircle className="h-12 w-12 text-red-500" />
                        </div>
                        <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-white">
                            {status === "timeout" ? "Ödeme henüz onaylanmadı" : "Ödeme tamamlanamadı"}
                        </h1>
                        <p className="text-slate-500 mt-2 max-w-md mx-auto">
                            {status === "timeout"
                                ? "Ödemeniz bankanız tarafından henüz işlenmemiş olabilir. Lütfen birkaç dakika sonra rezervasyonlarımdan kontrol edin."
                                : "Bir sorun oluştu. Lütfen tekrar deneyin veya destek ekibimizle iletişime geçin."}
                        </p>
                        <div className="mt-6 flex gap-3 justify-center">
                            <Link to="/" className="px-6 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white font-semibold">Ana Sayfa</Link>
                            {bookingId && (
                                <Link to={`/checkout?booking_id=${bookingId}`} className="px-6 py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-semibold">Tekrar Dene</Link>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
}

function Field({ icon, label, value }) {
    return (
        <div>
            <div className="flex items-center gap-1.5 text-xs text-slate-500 uppercase tracking-widest font-semibold mb-1">{icon}{label}</div>
            <div className="font-medium text-slate-900 dark:text-white">{value}</div>
        </div>
    );
}
